/**
 * iter-170s — Pricing resolver: pick the right price entry from a
 * multi-currency pricing map using the USER's profile currency, with a
 * deterministic fallback chain.
 *
 * Why: plans/packs/addons store ``pricing`` as
 * ``{AR: {amount: 1500, currency: "ARS"}, MX: {amount: 199, currency: "MXN"}, ...}``.
 * Pre-iter-170s, several call sites used ``Object.entries(pricing)[0]``
 * which surfaces whichever entry Mongo returned first — almost always
 * ARS, even for a user with currency = USD or MXN. That broke the
 * promise that "todo precio mostrado siempre toma la moneda del
 * usuario".
 *
 * Resolution order:
 *   1. Exact match on user's currency (e.g. profile.currency = "MXN" ⇒
 *      return the entry with currency "MXN").
 *   2. USD entry as a global fallback if present.
 *   3. ``null`` — caller decides what to render (typically a "Contactar
 *      soporte" CTA instead of a wrong price).
 */

/** Look up the pricing entry that matches the user's currency.
 *  @param {Record<string, {amount?: number|string, currency?: string}>} pricing
 *  @param {string|null|undefined} userCurrency e.g. "USD", "MXN".
 *  @returns {{entry: object, matched: boolean} | null}
 *    - ``matched=true``  ⇒ entry matches user's currency exactly.
 *    - ``matched=false`` ⇒ entry is a USD fallback.
 *    - ``null``          ⇒ no acceptable match (no USD either).
 */
export function resolvePriceForUser(pricing, userCurrency) {
  if (!pricing || typeof pricing !== "object") return null;
  const wanted = String(userCurrency || "").toUpperCase();

  // 1. Exact match
  if (wanted) {
    for (const [, v] of Object.entries(pricing)) {
      if (!v || !Number(v.amount)) continue;
      if (String(v.currency || "").toUpperCase() === wanted) {
        return { entry: v, matched: true };
      }
    }
  }

  // 2. USD fallback
  for (const [, v] of Object.entries(pricing)) {
    if (!v || !Number(v.amount)) continue;
    if (String(v.currency || "").toUpperCase() === "USD") {
      return { entry: v, matched: false };
    }
  }

  return null;
}

/** Format an amount as a currency string, with sensible locale defaults.
 *  Always returns a string; never throws. */
export function formatPrice(amount, currency, lang) {
  const ccy = String(currency || "USD").toUpperCase();
  const locale = lang === "en" ? "en-US" : (lang === "pt" ? "pt-BR" : "es-CO");
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: ccy,
      maximumFractionDigits: 0,
    }).format(Number(amount) || 0);
  } catch {
    // Unknown currency code → degrade to plain number + suffix.
    return `${ccy} ${Number(amount || 0).toLocaleString(locale)}`;
  }
}
